<template>
  <n-scrollable>

    <n-theme v-if="isAuthenticated" type="driver">
      <n-button @click="signOut" class="your-details-sign-out" inverted>
        {{ $t('bookRideDetails.signOut') }}
      </n-button>
    </n-theme>

    <n-layout v-if="hasPassengerCost" type="driver" :spacing-top="true" :top-gap="5">
      <n-text-model class="span-6" color="warning" size="lg" weight="regular">
        {{ $t('bookRideDetails.paymentRequired.title') }}
      </n-text-model>
      <n-text class="span-6" preset="body" white-space="pre-line">
        <i18n path="bookRideDetails.paymentRequired.body">
          <template v-slot:subsidyEligibility><a class="blue-link"
              :href="$t('bookRideDetails.paymentRequired.subsidyEligibilityUrl')"
              target="_blank">{{ $t('bookRideDetails.paymentRequired.subsidyEligibility') }}</a></template>
        </i18n>
      </n-text>


      <div class="span-6 store-links">
        <store-link store="google" />
        <store-link store="apple" />
      </div>
    </n-layout>

    <n-seperator v-if="hasPassengerCost" />

    <n-layout v-if="!hasPassengerCost">
      <n-text-model class="span-6" color="accent" size="xl" weight="regular">
        {{
          booked ? $t('bookRideOverview.title') : $t('bookRideDetails.title')
        }}
      </n-text-model>

      <n-text class="span-6" preset="body">
        {{
          booked
            ? $t('bookRideOverview.description')
            : $t('bookRideDetails.description')
        }}
      </n-text>

      <div v-if="driver" class="span-6 driver-container">
        <profile-picture :src="driver.image" />

        <div class="driver-info">
          <n-text preset="label-2" color="grey">
            {{ $t('bookRideDetails.driver') }}
          </n-text>
          <n-text preset="header" color="grey-dark">{{ fullname }}</n-text>
        </div>
      </div>

      <div class="span-6 ride-details">
        <n-text preset="label-2" color="grey-dark">{{
          $t('bookRideDetails.rideDetails')
        }}</n-text>

        <div class="ride-waypoints">
          <div class="ride-waypoint">
            <n-text
              class="ride-waypoint-time"
              preset="label-2"
              color="grey-dark"
            >
              {{ departureTimeFormatted }}
            </n-text>

            <div class="ride-waypoint-info">
              <n-text preset="body">{{ originAddressFormatted }}</n-text>
            </div>
          </div>

          <div class="ride-waypoint">
            <n-text
              class="ride-waypoint-time"
              preset="label-2"
              color="grey-dark"
            >
              {{ arrivalTimeFormatted }}
            </n-text>

            <div class="ride-waypoint-info">
              <n-text preset="body">{{ destinationAddressFormatted }}</n-text>

              <n-text preset="label-2" color="accent">
                <n-text
                  preset="label-2"
                  color="accent"
                  :striked="price != actualPrice"
                  >{{ $n(price, 'currency', currency) }}</n-text
                >
                <n-text
                  preset="label-2"
                  color="accent"
                  v-if="price != actualPrice"
                  > {{ $n(actualPrice, 'currency', currency) }}</n-text
                >
                |
                {{
                  $t('bookRideDetails.destinationSubtitle', {
                    km: $n(kilometers, 'km')
                  })
                }}
              </n-text>
            </div>
          </div>
        </div>
      </div>

      <discount-eligibility-warning
        :booking="bookingTrip"
        compact
      />

      <div v-if="isAuthenticated && !booked" class="span-6 your-details">
        <div class="your-details-header">
          <n-text preset="label-2" color="grey-dark">{{
            $t('bookRideDetails.yourDetails')
          }}</n-text>
        </div>

        <n-text preset="body">{{ userDetailsFormatted }}</n-text>
      </div>

      <n-grid :top-gap="4" v-if="booked">
        <n-button
          @click="bookNewTrip"
          class="span-3"
          type="outlined"
          inverted
          block
        >
          {{ $t('bookRideOverview.bookNewTrip') }}
        </n-button>

        <n-button
          @click="cancel"
          :loading="cancelling"
          class="span-3"
          type="outlined"
          color="error"
          inverted
          block
        >
          {{ $t('bookRideOverview.cancelBooking') }}
        </n-button>
      </n-grid>

      <n-button v-if="!hasPassengerCost && !booked" @click="book" class="book" block size="lg" :loading="loading">
        {{
          $t('bookRideDetails.book', {
            price: $n(actualPrice, 'currency', currency),
          })
        }}
      </n-button>
    </n-layout>

    <template v-if="booked">
      <n-seperator />

      <download-app />
    </template>
  </n-scrollable>
</template>

<script>
import store from '@/store';
import DownloadApp from './downloadApp';
import { mapActions, mapState } from 'vuex';
import StoreLink from '@/components/shared/links/storeLink';
import { format } from '@/vendor/date-fns';
import { parseJSON, isFuture } from 'date-fns';
import { triggerFormbricksAction } from '@/plugins/Formbricks';
import ProfilePicture from '@/components/shared/profilePicture';
import DiscountEligibilityWarning from '@/components/shared/tripDetails/discountEligibilityWarning';

const bookingLandingRoute = 'landing-book-ride.start';

export default {
  name: 'landingBookRideDetails',
  components: {
    DownloadApp,
    ProfilePicture,
    StoreLink,
    DiscountEligibilityWarning,
  },
  data() {
    return {
      loading: false,
      cancelling: false,
      hasPassengerCost: false,
    };
  },
  beforeRouteEnter(to, from, next) {
    const { bookingTrip } = store.state.trip;

    const bookableUntil = parseJSON(bookingTrip?.bookable_until);
    const bookableStill = isFuture(bookableUntil);

    if (!bookingTrip?.booked && !bookableStill) {
      next({ name: bookingLandingRoute });
      return;
    }

    next((vm) => {
      if (to.params?.booked) {
        vm.showBookingSuccess();
      }

      if (to.params?.hasPassengerCost) {
        vm.hasPassengerCost = true;
      }
    });
  },
  computed: {
    ...mapState('trip', ['bookingTrip']),
    ...mapState('user', ['profile', 'token']),
    driver() {
      return this.bookingTrip.driver;
    },
    fullname() {
      const names = [
        this.driver?.name,
        this.driver?.last_name,
      ];

      return names.filter((x) => x).join(' ');
    },
    kilometers() {
      const { distance } = this.bookingTrip.legs[0];
      return distance / 1000;
    },
    price() {
      return this.bookingTrip.price;
    },
    actualPrice() {
      return this.bookingTrip.actual_price;
    },
    currency() {
      return this.bookingTrip.price_currency;
    },
    booked() {
      return this.bookingTrip.booked || false;
    },
    departureTimeFormatted() {
      return format(this.bookingTrip.departure_time, 'HH:mm');
    },
    arrivalTimeFormatted() {
      return format(this.bookingTrip.arrival_time, 'HH:mm');
    },

    originAddressFormatted() {
      const { origin } = this.bookingTrip.legs[0];

      const values = [origin?.street || origin?.name, origin?.city];

      return values.filter((x) => x).join(', ');
    },
    destinationAddressFormatted() {
      const { destination } = this.bookingTrip.legs[0];

      const values = [
        destination?.street || destination?.name,
        destination?.city,
      ];

      return values.filter((x) => x).join(', ');
    },

    isAuthenticated() {
      return this.token !== null;
    },
    userDetailsFormatted() {
      const names = [this.profile?.name, this.profile?.last_name];

      const name = names.filter((x) => x).join(' ');

      if (name) {
        return name;
      }

      if (this.profile?.phone_number) {
        return `${this.profile.phoneCountry}${this.profile.phone_number}`;
      }

      return '-';
    },

  },
  methods: {
    ...mapActions('trip', ['bookTrip', 'cancelPassengerTrip']),
    ...mapActions('user', {
      signOut: 'SIGN_OUT',
    }),
    book() {
      const hasPassengerCost = this.actualPrice > 0;

      if (hasPassengerCost) {
        this.hasPassengerCost = true;
        return;
      }

      if (!this.isAuthenticated) {
        this.$router.push({
          name: 'landing-book-ride.login',
        });

        return;
      }

      if (this.loading) {
        return;
      }

      this.loading = true;

      this.bookTrip()
        .then((data) => {
          this.showBookingSuccess();

          const bookingContractId = data?.passenger_trip?.contract_id;

          triggerFormbricksAction('Action:InstantBooking:Created');
          triggerFormbricksAction(`Action:InstantBooking:Created:${bookingContractId}`);
        })
        .catch((e) => {
          const { identifier } = e.response?.data || {};
          const translationKey = `bookRideDetails.bookingErrors.${identifier}`;

          this.$error(
            this.$te(translationKey) ? this.$t(translationKey) : null
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancel() {
      if (this.cancelling) {
        return;
      }

      this.$modal.show('book-ride', {
        title: this.$t('bookRideOverview.dialogCancelBookingTitle'),
        text: this.$t('bookRideOverview.dialogCancelBookingDescription'),
        cancel: true,
        color: 'error',
        success: {
          text: this.$t('bookRideOverview.dialogCancelBookingConfirm'),
          handler: async () => {
            this.cancelling = true;

            this.cancelPassengerTrip(this.bookingTrip.passenger_trip.id)
              .then(() => {
                this.$success(this.$t('bookRideDetails.snackCancelBookingSuccess'));

                this.$router.replace({
                  name: bookingLandingRoute,
                });
              })
              .finally(() => {
                this.cancelling = false;
              });
          },
        },
      });
    },
    showBookingSuccess() {
      this.$success(this.$t('bookRideDetails.snackBookingSuccess'));
    },
    bookNewTrip() {
      this.$router.replace({
        name: bookingLandingRoute,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.driver-container {
  display: flex;
  margin-top: 1rem;
  flex-direction: row;

  .driver-info {
    margin-left: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.ride-details {
  margin-top: 1rem;
}

.ride-waypoints {
  margin-top: 0.25rem;

  .ride-waypoint {
    display: flex;
    flex-direction: row;
    margin: 0.25rem 0;

    &-time {
      padding: 0.35rem 0.7rem;
    }

    &-info {
      display: flex;
      flex-direction: column;

      span {
        margin: 0.15rem 0;
      }
    }
  }
}

.your-details {
  &-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  &-sign-out {
    flex: 0;
  }
}

.book {
  margin-top: 2rem;
}

.your-details-sign-out {
  top: 1.25rem;
  right: 0rem;
  width: auto;
  padding: 2rem var(--grid-padding-x);
  display: block;
  cursor: pointer;
  position: absolute;
  background-color: transparent;
}

a.blue-link {
  color: var(--color-blue);
  text-decoration: underline;
  cursor: pointer;
}

.store-links {
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 1.5rem;
  gap: 0.75rem;
  max-width: 100%;
}
</style>
